import React, { useState } from 'react';
import { Zap, Tv, Wifi, Phone, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { mockBillers, formatCurrency } from '@/utils/mockData';
import { toast } from 'sonner';

export default function BillsPage() {
  const [selectedBiller, setSelectedBiller] = useState('');
  const [amount, setAmount] = useState('');

  const handlePayment = (e) => {
    e.preventDefault();
    toast.success('Bill payment successful!');
  };

  const categories = [
    { id: 'electricity', icon: <Zap className="w-6 h-6" />, label: 'Electricity', color: 'bg-yellow-500' },
    { id: 'tv', icon: <Tv className="w-6 h-6" />, label: 'TV Subscription', color: 'bg-purple-500' },
    { id: 'internet', icon: <Wifi className="w-6 h-6" />, label: 'Internet', color: 'bg-blue-500' },
    { id: 'airtime', icon: <Phone className="w-6 h-6" />, label: 'Airtime', color: 'bg-green-500' },
    { id: 'data', icon: <Smartphone className="w-6 h-6" />, label: 'Data', color: 'bg-red-500' },
  ];

  return (
    <div className="max-w-4xl mx-auto" data-testid="bills-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold font-heading text-slate-900">Pay Bills</h1>
        <p className="text-slate-600 mt-1">Utilities, airtime, TV subscriptions, and more</p>
      </div>

      {/* Category Selection */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {categories.map((cat) => (
          <button 
            key={cat.id}
            data-testid={`bill-category-${cat.id}`}
            className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 hover:border-brand-blue hover:shadow-md transition-all"
          >
            <div className={`${cat.color} text-white w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2`}>
              {cat.icon}
            </div>
            <p className="text-sm font-medium text-slate-900 text-center">{cat.label}</p>
          </button>
        ))}
      </div>

      {/* Payment Form */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <Tabs defaultValue="electricity">
          <TabsList className="mb-6">
            <TabsTrigger value="electricity" data-testid="tab-electricity">Electricity</TabsTrigger>
            <TabsTrigger value="tv" data-testid="tab-tv">TV</TabsTrigger>
            <TabsTrigger value="airtime" data-testid="tab-airtime">Airtime</TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data">Data</TabsTrigger>
          </TabsList>

          <TabsContent value="electricity" className="space-y-4">
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Provider</Label>
                <Select>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="electricity-provider-select">
                    <SelectValue placeholder="Choose your provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockBillers.electricity.map((provider) => (
                      <SelectItem key={provider} value={provider.toLowerCase()}>
                        {provider}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="meter-number">Meter Number</Label>
                <Input 
                  id="meter-number" 
                  placeholder="Enter your meter number"
                  data-testid="meter-number-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="electricity-amount">Amount (₦)</Label>
                <Input 
                  id="electricity-amount" 
                  type="number" 
                  placeholder="0.00"
                  data-testid="electricity-amount-input"
                  required
                  min="500"
                  className="h-12 rounded-lg text-2xl font-bold"
                />
                <p className="text-sm text-slate-600">Min: ₦500 • No transaction fee</p>
              </div>

              <Button 
                type="submit"
                data-testid="pay-electricity-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Pay Bill
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="tv" className="space-y-4">
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Provider</Label>
                <Select>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="tv-provider-select">
                    <SelectValue placeholder="Choose your provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockBillers.tv.map((provider) => (
                      <SelectItem key={provider} value={provider.toLowerCase()}>
                        {provider}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="smartcard-number">Smartcard/IUC Number</Label>
                <Input 
                  id="smartcard-number" 
                  placeholder="Enter your smartcard number"
                  data-testid="smartcard-number-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label>Select Package</Label>
                <Select>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="tv-package-select">
                    <SelectValue placeholder="Choose package" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact - ₦10,500</SelectItem>
                    <SelectItem value="premium">Premium - ₦21,000</SelectItem>
                    <SelectItem value="confam">Confam - ₦7,400</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit"
                data-testid="pay-tv-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Subscribe Now
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="airtime" className="space-y-4">
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Network</Label>
                <div className="grid grid-cols-4 gap-3">
                  {mockBillers.airtime.map((network) => (
                    <button 
                      key={network}
                      type="button"
                      data-testid={`network-${network.toLowerCase()}`}
                      className="p-4 rounded-xl border-2 border-slate-200 hover:border-brand-blue transition-colors"
                    >
                      <p className="font-semibold text-slate-900">{network}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone-number">Phone Number</Label>
                <Input 
                  id="phone-number" 
                  type="tel"
                  placeholder="+234 801 234 5678"
                  data-testid="airtime-phone-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="airtime-amount">Amount (₦)</Label>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {[100, 200, 500, 1000].map((amt) => (
                    <button 
                      key={amt}
                      type="button"
                      onClick={() => setAmount(amt.toString())}
                      data-testid={`quick-amount-${amt}`}
                      className="p-3 rounded-xl bg-slate-100 hover:bg-brand-blue hover:text-white font-semibold transition-colors"
                    >
                      ₦{amt}
                    </button>
                  ))}
                </div>
                <Input 
                  id="airtime-amount" 
                  type="number" 
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="airtime-amount-input"
                  required
                  min="50"
                  className="h-12 rounded-lg text-2xl font-bold"
                />
              </div>

              <Button 
                type="submit"
                data-testid="buy-airtime-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Buy Airtime
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="data" className="space-y-4">
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Network</Label>
                <Select>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="data-network-select">
                    <SelectValue placeholder="Choose network" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockBillers.data.map((network) => (
                      <SelectItem key={network} value={network.toLowerCase()}>
                        {network}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="data-phone">Phone Number</Label>
                <Input 
                  id="data-phone" 
                  type="tel"
                  placeholder="+234 801 234 5678"
                  data-testid="data-phone-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label>Select Data Plan</Label>
                <Select>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="data-plan-select">
                    <SelectValue placeholder="Choose plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="500mb">500MB - 7 days - ₦500</SelectItem>
                    <SelectItem value="1gb">1GB - 30 days - ₦1,000</SelectItem>
                    <SelectItem value="2gb">2GB - 30 days - ₦2,000</SelectItem>
                    <SelectItem value="5gb">5GB - 30 days - ₦5,000</SelectItem>
                    <SelectItem value="10gb">10GB - 30 days - ₦10,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit"
                data-testid="buy-data-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Buy Data
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>

      {/* Recent Bills */}
      <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold font-heading text-slate-900 mb-4">Recent Bill Payments</h3>
        <div className="space-y-3">
          {[
            { name: 'EKEDC - Electricity', amount: 3500, date: 'Dec 18, 2024' },
            { name: 'DSTV Premium', amount: 12500, date: 'Dec 19, 2024' },
            { name: 'MTN Airtime', amount: 5000, date: 'Dec 20, 2024' }
          ].map((bill, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
              <div>
                <p className="font-semibold text-slate-900">{bill.name}</p>
                <p className="text-sm text-slate-600">{bill.date}</p>
              </div>
              <p className="font-bold text-slate-900">{formatCurrency(bill.amount)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
